/**
 * Lasting Grass product page behaviour. Owner: product agent.
 * Port of the client side of src/components/ProductConfigurator.tsx.
 *
 * Loaded with defer by sections/lg-product-main.liquid, after lg-core.js, so window.LG
 * exists. Everything here is an enhancement: without this file the buy box is a plain
 * Shopify product form (radio buttons named "id", select named properties[Colour],
 * input named "quantity") and the "What do I need?" tab stays out of sight.
 *
 * What it does
 *   - binds the length pills to the variant JSON that snippets/lg-product-buybox.liquid
 *     prints ([data-lg-product-json]): ids, lengths in metres, prices in cents
 *   - keeps ?variant=<id> in the address bar in step with the selected length
 *   - updates the "qty x width x length (area each)" line, the cutting fee note and the
 *     total inc. GST when the length, rolls or colour change
 *   - clamps rolls to 1..50 (same as the source)
 *   - sends the colour (Coolplay Green) as the line item property Colour
 *   - adds to cart with LG.addToCart, then confirms with a link to the cart, or shows an
 *     error message and leaves the button usable
 *   - the "What do I need?" helper: width and length in metres to strips to a suggested
 *     length, with the same arithmetic as the source, and a button that fills the order tab
 *
 * Vanilla ES2020, no libraries. Tailwind reads the class names in this file, so they are
 * written out in full (see docs/shopify/theme-conventions.md, section 7).
 */
(function () {
  "use strict";

  var DEFAULT_MAX_QTY = 50;

  // Tab button classes: the source's tabClass(active). Written out so Tailwind sees them.
  var TAB_BASE =
    "inline-flex min-h-11 flex-1 items-center justify-center rounded-full px-4 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-turf-600";
  var TAB_ON = "bg-turf-600 text-white shadow-sm";
  var TAB_OFF = "bg-transparent text-charcoal/70 hover:bg-white hover:text-turf-700";

  // Status messages under the Add to cart button.
  var NOTE_OK_CLASS =
    "mt-3 flex items-center justify-between rounded-lg bg-turf-50 px-4 py-3 text-sm font-medium text-turf-800";
  var NOTE_OK_LINK_CLASS =
    "rounded font-semibold text-turf-700 underline underline-offset-4 hover:text-turf-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-turf-600";
  var NOTE_ERROR_CLASS =
    "mt-3 rounded-lg border-l-4 border-sun bg-sun-tint px-4 py-3 text-sm font-medium text-charcoal";

  // ---------------------------------------------------------------------------
  // formatting and arithmetic (same as src/components/format.ts and ProductConfigurator.tsx)
  // ---------------------------------------------------------------------------

  /** "3.71m": metres, up to 2 decimal places, en-AU grouping. */
  function formatMetres(m) {
    var rounded = Math.round(m * 100) / 100;
    return rounded.toLocaleString("en-AU", { maximumFractionDigits: 2 }) + "m";
  }

  /** "74.2 m²": up to 2 decimal places. */
  function formatSqm(sqm) {
    var rounded = Math.round(sqm * 100) / 100;
    return rounded.toLocaleString("en-AU", { maximumFractionDigits: 2 }) + " m²";
  }

  var moneyFallback = null;
  function formatMoney(cents) {
    if (window.LG && typeof window.LG.formatMoney === "function") return window.LG.formatMoney(cents);
    if (!moneyFallback) {
      try {
        moneyFallback = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });
      } catch (e) {
        moneyFallback = { format: function (n) { return "$" + n.toFixed(2); } };
      }
    }
    return moneyFallback.format(Number(cents) / 100);
  }

  function parsePositive(value) {
    var n = Number.parseFloat(value);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  function clampQty(raw, max) {
    var n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? Math.min(Math.max(n, 1), max) : 1;
  }

  /**
   * The helper's plan. Verbatim from the source's helperPlan useMemo: orient so the roll's
   * fixed width covers the shorter side, one strip per roll width across, and the shortest
   * listed length that covers the run (or the longest, when none does).
   */
  function helperPlan(widthStr, lengthStr, rollWidth, cutLengths) {
    var a = parsePositive(widthStr);
    var b = parsePositive(lengthStr);
    if (a <= 0 || b <= 0 || !cutLengths.length) return null;
    var stripsAcross = Math.max(1, Math.ceil(Math.min(a, b) / rollWidth));
    var runLengthM = Math.max(a, b);
    var suggestedLength = cutLengths.find(function (l) { return l >= runLengthM; });
    if (suggestedLength === undefined) suggestedLength = cutLengths[cutLengths.length - 1];
    var rollsNeeded = stripsAcross;
    return {
      stripsAcross: stripsAcross,
      runLengthM: runLengthM,
      suggestedLength: suggestedLength,
      rollsNeeded: rollsNeeded,
    };
  }

  // ---------------------------------------------------------------------------
  // setup
  // ---------------------------------------------------------------------------

  function readData(root) {
    var el = root.querySelector("[data-lg-product-json]");
    if (!el) return null;
    try {
      var data = JSON.parse(el.textContent);
      return data && Array.isArray(data.variants) ? data : null;
    } catch (e) {
      return null;
    }
  }

  function init(root) {
    var data = readData(root);
    if (!data) return;

    var rollWidth = Number(data.rollWidth) || 3.71;
    var maxQty = Number(data.maxQty) || DEFAULT_MAX_QTY;
    var cartUrl = data.cartUrl || "/cart";
    var variantsById = {};
    var cutLengths = [];
    data.variants.forEach(function (v) {
      variantsById[String(v.id)] = v;
      if (typeof v.length === "number" && v.length > 0 && cutLengths.indexOf(v.length) === -1) {
        cutLengths.push(v.length);
      }
    });
    cutLengths.sort(function (x, y) { return x - y; });
    var fullRollLength = cutLengths.length ? cutLengths[cutLengths.length - 1] : 0;

    var form = root.querySelector("[data-lg-form]");
    var qtyInput = root.querySelector("#qty");
    var colourSelect = root.querySelector("#colour");
    var summaryEl = root.querySelector("[data-lg-summary]");
    var feeEl = root.querySelector("[data-lg-fee]");
    var totalEl = root.querySelector("[data-lg-total]");
    var addButton = root.querySelector("[data-lg-add]");
    var statusEl = root.querySelector("[data-lg-status]");
    var variantInputs = Array.prototype.slice.call(root.querySelectorAll("[data-lg-variant]"));
    if (!form || !qtyInput || !variantInputs.length) return;

    // The source never blocks on the quantity: clampQty turns a blank, 0, 2.5 or 51 into 1 to
    // 50 and adds. Native min, max and step checks would stop the submit before our handler
    // runs, so with the core script present they are switched off. Without it the form stays a
    // plain product form and keeps the browser's validation.
    if (window.LG && typeof window.LG.addToCart === "function") form.noValidate = true;

    var addLabel = "Add to cart";
    var pending = false;

    // -- selected variant ------------------------------------------------------

    function currentVariant() {
      var chosen = null;
      variantInputs.forEach(function (input) {
        if (input.type === "radio") {
          if (input.checked) chosen = input;
        } else {
          chosen = input;
        }
      });
      return chosen ? variantsById[String(chosen.value)] || null : null;
    }

    function selectLength(length) {
      var match = null;
      variantInputs.forEach(function (input) {
        var v = variantsById[String(input.value)];
        if (v && v.length === length) match = input;
      });
      if (!match) return;
      match.checked = true;
    }

    function syncUrl(variant) {
      if (!variant) return;
      try {
        var url = new URL(window.location.href);
        url.searchParams.set("variant", String(variant.id));
        window.history.replaceState(window.history.state, "", url.toString());
      } catch (e) {
        /* the address bar is a nicety, ignore */
      }
    }

    // -- status line under the button ------------------------------------------

    function clearStatus() {
      if (statusEl) statusEl.textContent = "";
    }

    function showAdded() {
      if (!statusEl) return;
      statusEl.textContent = "";
      var note = document.createElement("p");
      note.className = NOTE_OK_CLASS;
      note.appendChild(document.createTextNode("Added to cart."));
      var link = document.createElement("a");
      link.href = cartUrl;
      link.className = NOTE_OK_LINK_CLASS;
      link.textContent = "View cart →";
      note.appendChild(link);
      statusEl.appendChild(note);
    }

    function showError(message) {
      if (!statusEl) return;
      statusEl.textContent = "";
      var note = document.createElement("p");
      note.className = NOTE_ERROR_CLASS;
      note.setAttribute("role", "alert");
      note.textContent = message;
      statusEl.appendChild(note);
    }

    // -- render the order panel ------------------------------------------------

    function render() {
      var variant = currentVariant();
      var qty = clampQty(qtyInput.value, maxQty);

      if (variant && typeof variant.length === "number" && variant.length > 0) {
        var isFull = variant.length === fullRollLength;
        if (summaryEl) {
          summaryEl.textContent =
            qty +
            " × " +
            formatMetres(rollWidth) +
            " × " +
            formatMetres(variant.length) +
            " (" +
            formatSqm(rollWidth * variant.length) +
            " each)";
        }
        // The cutting fee is already inside the variant price; this only explains it.
        if (feeEl) feeEl.hidden = isFull || !(Number(data.cuttingFeeCents) > 0);
      } else if (summaryEl) {
        summaryEl.textContent = "";
        if (feeEl) feeEl.hidden = true;
      }

      if (totalEl && variant) totalEl.textContent = formatMoney(variant.price * qty);

      if (addButton && !pending) {
        var available = !!(variant && variant.available);
        addButton.disabled = !available;
        addButton.textContent = available ? addLabel : "Sold out";
      }
    }

    variantInputs.forEach(function (input) {
      input.addEventListener("change", function () {
        clearStatus();
        syncUrl(currentVariant());
        render();
      });
    });

    qtyInput.addEventListener("input", function () {
      clearStatus();
      render();
    });
    // Raw text while typing so the field can be cleared; on blur show the clamped value.
    qtyInput.addEventListener("blur", function () {
      qtyInput.value = String(clampQty(qtyInput.value, maxQty));
      render();
    });

    if (colourSelect) {
      colourSelect.addEventListener("change", function () {
        clearStatus();
      });
    }

    // -- add to cart ----------------------------------------------------------

    form.addEventListener("submit", function (event) {
      // No core script: let the browser post the form to /cart/add as a plain form.
      if (!window.LG || typeof window.LG.addToCart !== "function") return;
      event.preventDefault();

      var variant = currentVariant();
      if (!variant || !variant.available || pending) return;

      var qty = clampQty(qtyInput.value, maxQty);
      qtyInput.value = String(qty);
      var properties = {};
      if (colourSelect && colourSelect.value) properties.Colour = colourSelect.value;

      pending = true;
      clearStatus();
      if (addButton) {
        addButton.disabled = true;
        addButton.setAttribute("aria-busy", "true");
      }

      function done() {
        pending = false;
        if (addButton) addButton.removeAttribute("aria-busy");
        render();
      }

      Promise.resolve(window.LG.addToCart({ id: variant.id, quantity: qty, properties: properties }))
        .then(function (result) {
          if (result && result.ok) {
            showAdded();
          } else {
            var detail = result && (result.description || result.message);
            showError(
              "Sorry, that could not be added to your cart." + (detail ? " " + detail : " Please try again.")
            );
          }
        })
        .catch(function () {
          showError("Sorry, that could not be added to your cart. Please try again.");
        })
        .then(done, done);
    });

    // -- tabs -----------------------------------------------------------------

    var tablist = root.querySelector("[data-lg-tablist]");
    var tabs = {
      order: root.querySelector('[data-lg-tab="order"]'),
      helper: root.querySelector('[data-lg-tab="helper"]'),
    };
    var panels = {
      order: root.querySelector('[data-lg-panel="order"]'),
      helper: root.querySelector('[data-lg-panel="helper"]'),
    };
    var tabOrder = ["order", "helper"];
    var activeTab = "order";

    function selectTab(name, focus) {
      if (!tabs[name] || !panels[name]) return;
      activeTab = name;
      tabOrder.forEach(function (key) {
        var on = key === name;
        tabs[key].setAttribute("aria-selected", on ? "true" : "false");
        tabs[key].setAttribute("tabindex", on ? "0" : "-1");
        tabs[key].className = TAB_BASE + " " + (on ? TAB_ON : TAB_OFF);
        panels[key].hidden = !on;
      });
      clearStatus();
      if (focus) tabs[name].focus();
    }

    if (tablist && tabs.order && tabs.helper && panels.order && panels.helper) {
      tabOrder.forEach(function (key) {
        tabs[key].addEventListener("click", function () {
          selectTab(key, false);
        });
        tabs[key].addEventListener("keydown", function (event) {
          var i = tabOrder.indexOf(activeTab);
          var next = null;
          if (event.key === "ArrowRight" || event.key === "ArrowDown") next = tabOrder[(i + 1) % tabOrder.length];
          else if (event.key === "ArrowLeft" || event.key === "ArrowUp") next = tabOrder[(i - 1 + tabOrder.length) % tabOrder.length];
          else if (event.key === "Home") next = tabOrder[0];
          else if (event.key === "End") next = tabOrder[tabOrder.length - 1];
          if (!next) return;
          event.preventDefault();
          selectTab(next, true);
        });
      });
      tablist.classList.remove("invisible");
    }

    // -- "What do I need?" helper -----------------------------------------------

    var helperWidth = root.querySelector("#helper-a");
    var helperLength = root.querySelector("#helper-b");
    var helperResult = root.querySelector("[data-lg-helper-result]");
    var helperPlanEl = root.querySelector("[data-lg-helper-plan]");
    var helperApply = root.querySelector("[data-lg-helper-apply]");
    var plan = null;

    function updateHelper() {
      if (!helperWidth || !helperLength || !helperResult || !helperPlanEl) return;
      plan = helperPlan(helperWidth.value, helperLength.value, rollWidth, cutLengths);
      if (!plan) {
        helperResult.hidden = true;
        helperPlanEl.textContent = "";
        return;
      }
      helperPlanEl.textContent =
        "Rough plan: " +
        plan.rollsNeeded +
        " strip" +
        (plan.rollsNeeded === 1 ? "" : "s") +
        " at " +
        formatMetres(rollWidth) +
        " wide, each a " +
        formatMetres(plan.suggestedLength) +
        " length.";
      helperResult.hidden = false;
    }

    if (helperWidth) helperWidth.addEventListener("input", updateHelper);
    if (helperLength) helperLength.addEventListener("input", updateHelper);

    if (helperApply) {
      helperApply.addEventListener("click", function () {
        if (!plan) return;
        selectLength(cutLengths.indexOf(plan.suggestedLength) !== -1 ? plan.suggestedLength : fullRollLength);
        qtyInput.value = String(Math.min(plan.rollsNeeded, maxQty));
        syncUrl(currentVariant());
        selectTab("order", true);
        render();
      });
    }

    render();
  }

  function boot() {
    Array.prototype.slice.call(document.querySelectorAll("[data-lg-product]")).forEach(init);
  }

  if (window.LG && typeof window.LG.ready === "function") {
    window.LG.ready(boot);
  } else if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }
})();
